'use client';

import React from 'react';
import {
  MapPin,
  Mail,
  FileCheck,
  Globe2,
  Shield,
  Clock,
  Check,
  Phone,
  Landmark,
  Mailbox,
  Archive,
  UserCheck,
  Headphones,
  Scale,
  Leaf,
  Presentation,
  Truck,
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export default function VirtualOfficeSection() {
  const { t } = useLanguage();

  const BENEFITS = [
    { id: 'vob-1', icon: MapPin, titleKey: 'vo.benefit1.title', descKey: 'vo.benefit1.desc' },
    { id: 'vob-2', icon: Mail, titleKey: 'vo.benefit2.title', descKey: 'vo.benefit2.desc' },
    { id: 'vob-3', icon: FileCheck, titleKey: 'vo.benefit3.title', descKey: 'vo.benefit3.desc' },
    { id: 'vob-4', icon: Globe2, titleKey: 'vo.benefit4.title', descKey: 'vo.benefit4.desc' },
    { id: 'vob-5', icon: Shield, titleKey: 'vo.benefit5.title', descKey: 'vo.benefit5.desc' },
    { id: 'vob-6', icon: Clock, titleKey: 'vo.benefit6.title', descKey: 'vo.benefit6.desc' },
  ];

  const EXTRA_BENEFITS = [
    { id: 'vox-1', icon: Shield, titleKey: 'vo.extraBenefits.1.title', descKey: 'vo.extraBenefits.1.desc' },
    { id: 'vox-2', icon: Phone, titleKey: 'vo.extraBenefits.2.title', descKey: 'vo.extraBenefits.2.desc' },
    { id: 'vox-3', icon: Landmark, titleKey: 'vo.extraBenefits.3.title', descKey: 'vo.extraBenefits.3.desc' },
    { id: 'vox-4', icon: Mailbox, titleKey: 'vo.extraBenefits.4.title', descKey: 'vo.extraBenefits.4.desc' },
    { id: 'vox-5', icon: Archive, titleKey: 'vo.extraBenefits.5.title', descKey: 'vo.extraBenefits.5.desc' },
    { id: 'vox-6', icon: UserCheck, titleKey: 'vo.extraBenefits.6.title', descKey: 'vo.extraBenefits.6.desc' },
  ];

  const PACK_FEATURE_KEYS = [
    'vo.pack.f1',
    'vo.pack.f2',
    'vo.pack.f3',
    'vo.pack.f4',
    'vo.pack.f5',
    'vo.pack.f6',
    'vo.pack.f7',
  ] as const;

  const ADD_ON_SERVICES = [
    {
      id: 'voa-1',
      icon: Headphones,
      titleKey: 'vo.addOns.1.title',
      descKey: 'vo.addOns.1.desc',
      priceKey: 'vo.addOns.1.price',
    },
    {
      id: 'voa-2',
      icon: Scale,
      titleKey: 'vo.addOns.2.title',
      descKey: 'vo.addOns.2.desc',
      priceKey: 'vo.addOns.2.price',
    },
    {
      id: 'voa-3',
      icon: Leaf,
      titleKey: 'vo.addOns.3.title',
      descKey: 'vo.addOns.3.desc',
      priceKey: 'vo.addOns.3.price',
    },
    {
      id: 'voa-4',
      icon: Mailbox,
      titleKey: 'vo.addOns.4.title',
      descKey: 'vo.addOns.4.desc',
      priceKey: 'vo.addOns.4.price',
    },
    {
      id: 'voa-5',
      icon: Presentation,
      titleKey: 'vo.addOns.5.title',
      descKey: 'vo.addOns.5.desc',
      priceKey: 'vo.addOns.5.price',
    },
    {
      id: 'voa-6',
      icon: Truck,
      titleKey: 'vo.addOns.6.title',
      descKey: 'vo.addOns.6.desc',
      priceKey: 'vo.addOns.6.price',
    },
  ] as const;

  return (
    <section id="virtual-office" className="py-20 lg:py-28 bg-white">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-xs font-semibold text-[#C9A96E] uppercase tracking-widest mb-3">{t('vo.tag')}</p>
          <h2 className="font-display text-4xl font-bold text-[#1B3A5C] tracking-tight mb-4">
            {t('vo.sectionTitle')?.split('\n')?.map((line, i) => (
              <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
            ))}
          </h2>
          <p className="text-slate-500 text-lg leading-relaxed max-w-2xl mx-auto">
            {t('vo.sectionDesc')}
          </p>
        </div>

        {/* Benefits grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {BENEFITS?.map((b) => {
            const Icon = b?.icon;
            return (
              <div key={b?.id} className="flex gap-3.5 p-5 rounded-xl bg-slate-50 border border-slate-100 hover:border-[#1B3A5C]/20 hover:bg-[#1B3A5C]/2 transition-all duration-150 group">
                <div className="w-10 h-10 rounded-xl bg-[#E8EDF4] flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-[#dce4f0] transition-colors">
                  <Icon size={17} className="text-[#1B3A5C]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800 mb-1">{t(b?.titleKey)}</p>
                  <p className="text-xs text-slate-500 leading-relaxed">{t(b?.descKey)}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mb-12 w-full rounded-2xl border border-[#1B3A5C]/12 bg-[#f8fafc] px-5 py-4 sm:px-8 sm:py-5">
          <p className="text-sm sm:text-base font-semibold text-[#1B3A5C] text-center leading-snug">
            {t('vo.legalHighlight')}
          </p>
        </div>

        {/* Pricing — single package */}
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
            <div>
              <h3 className="font-display font-bold text-xl text-[#1B3A5C] mb-1">{t('vo.plansTitle')}</h3>
              <p className="text-sm text-slate-500">{t('vo.plansDesc')}</p>
            </div>
            <a href="#contact" className="btn-primary self-start lg:self-auto">
              {t('vo.requestQuote')}
            </a>
          </div>

          <div className="max-w-xl mx-auto">
            <div className="rounded-xl p-6 border transition-all duration-200 flex flex-col bg-[#1B3A5C] border-[#1B3A5C] shadow-lg">
              <h4 className="font-display font-bold text-lg mb-1 text-white">{t('vo.pack.title')}</h4>
              <p className="text-xs leading-snug mb-4 text-slate-300">{t('vo.pack.tagline')}</p>
              <div className="mb-4">
                <div className="flex items-end gap-2 flex-wrap">
                  <span className="font-display font-bold text-3xl tabular-nums text-white">{t('vo.pack.priceRsd')}</span>
                  <span className="font-display font-semibold text-xl tabular-nums text-slate-200">{t('vo.pack.priceEur')}</span>
                  <span className="text-sm mb-1 text-slate-300">{t('vo.month')}</span>
                </div>
                <p className="text-sm font-medium text-[#C9A96E] mt-2">{t('vo.pack.annualNote')}</p>
              </div>
              <ul className="space-y-2.5 flex-1 text-slate-200">
                {PACK_FEATURE_KEYS.map((fk) => (
                  <li key={fk} className="flex items-start gap-2.5 text-sm leading-snug text-slate-200">
                    <Check size={16} className="shrink-0 mt-0.5 text-[#C9A96E]" strokeWidth={2.5} aria-hidden />
                    <span>{t(fk)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Dodatne usluge (umesto pune liste lokacija) */}
          <div className="mt-12 pt-12 border-t border-slate-200">
            <div className="mb-8">
              <p className="text-xs font-semibold text-[#C9A96E] uppercase tracking-widest mb-3">{t('vo.addOns.tag')}</p>
              <h4 className="font-display font-bold text-xl sm:text-2xl text-[#1B3A5C] tracking-tight mb-2">{t('vo.addOns.title')}</h4>
              <p className="text-sm text-slate-500 max-w-2xl leading-relaxed">{t('vo.addOns.subtitle')}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {ADD_ON_SERVICES.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.id}
                    className="flex gap-3.5 p-5 rounded-xl bg-white border border-slate-200/90 shadow-sm hover:border-[#1B3A5C]/25 transition-all duration-150 group h-full"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#E8EDF4] flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-[#dce4f0] transition-colors">
                      <Icon size={17} className="text-[#1B3A5C]" aria-hidden />
                    </div>
                    <div className="flex-1 flex flex-col min-w-0">
                      <p className="text-sm font-semibold text-slate-800 mb-1">{t(item.titleKey)}</p>
                      <p className="text-xs text-slate-500 leading-relaxed flex-1">{t(item.descKey)}</p>
                      <p className="mt-3 self-end rounded-md border border-[#1B3A5C]/15 bg-slate-50 px-2.5 py-1.5 text-xs font-bold text-[#1B3A5C] shadow-sm text-right leading-snug tabular-nums">
                        {t(item.priceKey)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Benefits of virtual office (below plans) */}
        <div className="mt-16 lg:mt-20">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold text-[#C9A96E] uppercase tracking-widest mb-3">{t('vo.extraBenefits.tag')}</p>
            <h2 className="font-display text-4xl font-bold text-[#1B3A5C] tracking-tight mb-4">{t('vo.extraBenefits.title')}</h2>
            <p className="text-slate-500 text-lg leading-relaxed max-w-2xl mx-auto">{t('vo.extraBenefits.desc')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {EXTRA_BENEFITS?.map((b) => {
              const Icon = b?.icon;
              return (
                <div
                  key={b?.id}
                  className="flex gap-3.5 p-5 rounded-xl bg-slate-50 border border-slate-100 hover:border-[#1B3A5C]/20 hover:bg-[#1B3A5C]/2 transition-all duration-150 group"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#E8EDF4] flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-[#dce4f0] transition-colors">
                    <Icon size={17} className="text-[#1B3A5C]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 mb-1">{t(b?.titleKey)}</p>
                    <p className="text-xs text-slate-500 leading-relaxed">{t(b?.descKey)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
